import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../../Component/Table";
import {
  getAllMasterTags,
  createMasterTag,
  updateMasterTag,
  activateDeactivateMasterTags,
} from "../../Services/tags"
import DebouncedInput from "../../Component/DebouncedInput";
import { formatDate } from "../../utils/date";
import { TagsOutlined } from "@ant-design/icons";
import { Button, Flex, Modal, Tag, Tooltip  } from "antd";
import AddModal from "./AddModal";
import EditModal from "./EditModal";
import TagMapUserModal from "./TagMapUserModal"


const columns = (showEditModal, handleToggleActive, handleTagMapClick) => [
  {
    title: "#ID",
    dataIndex: "id",
    key: "id",
  },
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render: (text) =>
      text &&text.length > 20 ? (
        <Tooltip title={text}>
          {text.slice(0, 20) + "..."}
        </Tooltip>
      ) : (
        text
      ),
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    render: (text) =>
      text && text.length > 30 ? (
        <Tooltip title={text}>
          {text.slice(0, 30) + "..."}
        </Tooltip>
      ) : (
        text
      ),
  },
  {
    title: "User Count",
    dataIndex: "user_count",
    key: "user_count",
  },
  {
    title: "Active",
    dataIndex: "active",
    key: "active",
    render: (text, record) => (
      <>
        <Tag color={record?.active ? "green" : "red"}>
          {record?.active ? "Yes" : "No"}
        </Tag>
      </>
    ),
  },
  {
    title: "Created At",
    dataIndex: "created_at",
    key: "created_at",
    render: (text, record) =>
      record?.created_at ? formatDate(record.created_at) : "-",
  },
  {
    title: "Updated At",
    dataIndex: "updated_at",
    key: "updated_at",
    render: (text, record) =>
      record?.updated_at ? formatDate(record.updated_at) : "-",
  },
  {
    title: "Actions",
    key: "actions",
    fixed: 'right',
    render: (_, record) => (
      <Flex gap="small">
        <Tooltip title="Map User to Tag">
          <Button color="primary" variant="link" icon={<TagsOutlined />} onClick={() => handleTagMapClick(record)}></Button>
        </Tooltip>
        
        <Button type="link" size="small" onClick={() => showEditModal(record)}>
          Edit
        </Button>
        <Button
          variant="link"
          size="small"
          color={record?.active ? "danger" : "primary"}
          onClick={() => handleToggleActive(record)}
        >
          {" "}
          {record?.active ? "Deactivate" : "Activate"}
        </Button>
      </Flex>
    ),
  },
];

function MasterTags() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [tagMapModalVisible, setTagMapModalVisible] = useState(false)

  const showAddModal = () => {
    setIsAddModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingRecord(record);
    setIsEditModalVisible(true);
  };

  const handleTagMapClick = (record) => {
    setEditingRecord(record);
    setTagMapModalVisible(true);
  };

  
  useEffect(() => {
    setCurrentPage(1); 
  }, [searchTerm]);
  
  const skip = (currentPage - 1) * pageSize;

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["mas_tag", currentPage, pageSize, searchTerm],
    queryFn: () =>
      getAllMasterTags({
        skip,
        limit: pageSize,
        name: searchTerm,
      }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const addMutation = useMutation({
    mutationFn: createMasterTag,
    onSuccess: (data) => {
        if(data){
            refetch();
            setIsAddModalVisible(false);
        }
    },
  });

  const editMutation = useMutation({
    mutationFn: updateMasterTag,
    onSuccess: (data) => {
        if(data){
            refetch();
            setIsEditModalVisible(false);
        }
    },
  });

  const handleAddSubmit = (values) => {
    addMutation.mutate(values);
  };

  const handleEditSubmit = (id, values) => {
    editMutation.mutate({ id: id, ...values });
  };

  const activateMutation = useMutation({
    mutationFn: (data) =>
      activateDeactivateMasterTags(data.id, data.active),
    onSuccess: () => {
      queryClient.invalidateQueries(["mas_tag"]); // Refetch user list after update
    },
  });

  const handleToggleActive = (master_tag) => {
    Modal.confirm({
      title: "Confirm",
      content: `Are you sure you want to ${
        master_tag.active ? "deactivate" : "activate"
      } this Master Tag?`,
      okText: master_tag.active ? "Deactivate" : "Activate",
      cancelText: "Cancel",
      onOk: () => {
        activateMutation.mutate({
          id: master_tag.id,
          active: !master_tag.active, // Toggle Active Status
        });
      },
    });
  };

  return (
    <div>
      <h3>Master Tags</h3>
      <Flex justify="space-between">
        <DebouncedInput
          placeholder="Search by Tag..."
          value={searchTerm}
          onChange={setSearchTerm}
          delay={1000}
        />
        <Button type="primary" onClick={showAddModal}>
          Add Master Tag
        </Button>
      </Flex>
      <br />
      <CustomTable
        columns={columns(showEditModal, handleToggleActive, handleTagMapClick)}
        data={data?.master_tags || []}
        loading={isLoading || isFetching}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: data?.total_count || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
        }}
        onTableChange={handleTableChange}
        maxHeight={"calc(90vh - 180px)"}
      />
      <AddModal
        isModalVisible={isAddModalVisible}
        handleOk={handleAddSubmit}
        handleCancel={() => setIsAddModalVisible(false)}
        loading={addMutation.isPending}
      />
      <EditModal
        isModalVisible={isEditModalVisible}
        handleOk={handleEditSubmit}
        handleCancel={() => setIsEditModalVisible(false)}
        loading={editMutation.isPending}
        initialValues={editingRecord}
      />
      <TagMapUserModal
        TagMapVisible={tagMapModalVisible}
        onClose={() => {setTagMapModalVisible(false);refetch();}}
        masterTag={editingRecord}
      />
    </div>
  );
}

export default MasterTags;
